// src/components/Playground.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../lib/api';

interface PlaygroundProps {
  token: string;
}

type Role = 'user' | 'assistant' | 'system';
type ConvStatus = 'active' | 'archived';
type ChatbotType = 'Default' | 'ConversationalChatbot';
type DomainType =
  | 'saas'
  | 'real_estate'
  | 'healthcare'
  | 'education'
  | 'retail'
  | 'automobile'
  | 'travel'
  | 'finance_insurance'
  | 'legal_services'
  | 'home_services'
  | 'manufacturing'
  | 'wholesale_distribution'
  | 'hospitality'
  | 'beauty_wellness'
  | 'fitness'
  | 'events_wedding_services'
  | 'printing_signage'
  | 'packaging_labeling'
  | 'it_services'
  | 'electronics_appliances'
  | 'furniture_interiors'
  | 'agri_inputs';

interface KnowledgeBase {
  id: number;
  name: string;
  filePath: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Prompt {
  id: number;
  name: string;
  isActive: boolean;
  isSystemDefined: boolean;
  userId: number | null;
}

interface Conversation {
  id: string;
  kbId: number;
  title: string | null;
  status: ConvStatus;
  messageCount: number;
  summary: string | null;
  externalConversationId: string | null;
  channel: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Message {
  id: number;
  role: Role;
  content: string;
  metadata?: any;
  createdAt: string;
}

type SchedulingTerms = { book: string[]; update: string[]; cancel: string[]; info: string[] };

interface DomainContextPreset {
  industry: string;
  business_type: string;
  company_description: string;
  products_or_services: string[];
  domain_vocabulary: string[];
  user_goals: string[];
  important_entities: string[];
  tone_guidelines: string[];
}

interface DomainSettingsPreset {
  label: string;
  terms: SchedulingTerms;
  domainContext: DomainContextPreset;
}

interface DomainContextFormState {
  industry: string;
  businessType: string;
  companyDescription: string;
  productsOrServices: string;
  domainVocabulary: string;
  userGoals: string;
  importantEntities: string;
  toneGuidelines: string;
}

interface PlaygroundCompanySettingsState {
  scheduleLabel: string;
  schedulingTerms: {
    book: string;
    update: string;
    cancel: string;
    info: string;
  };
  domainContext: DomainContextFormState;
}

const DOMAIN_OPTIONS: { value: DomainType; label: string }[] = [
  { value: 'saas', label: 'SaaS (Demo)' },
  { value: 'real_estate', label: 'Real Estate (Site Visit)' },
  { value: 'healthcare', label: 'Healthcare (Appointment)' },
  { value: 'education', label: 'Education (Counseling Session)' },
  { value: 'retail', label: 'Retail (Store Visit)' },
  { value: 'automobile', label: 'Automobile (Test Drive)' },
  { value: 'travel', label: 'Travel (Travel Consultation)' },
  { value: 'finance_insurance', label: 'Finance & Insurance (Consultation)' },
  { value: 'legal_services', label: 'Legal Services (Legal Consultation)' },
  { value: 'home_services', label: 'Home Services (Service Visit)' },
  { value: 'manufacturing', label: 'Manufacturing (Business Meeting)' },
  { value: 'wholesale_distribution', label: 'Wholesale & Distribution (Dealer Meeting)' },
  { value: 'hospitality', label: 'Hospitality (Booking Consultation)' },
  { value: 'beauty_wellness', label: 'Beauty & Wellness (Appointment)' },
  { value: 'fitness', label: 'Fitness (Trial Session)' },
  { value: 'events_wedding_services', label: 'Events & Wedding Services (Consultation)' },
  { value: 'printing_signage', label: 'Printing & Signage (Project Discussion)' },
  { value: 'packaging_labeling', label: 'Packaging & Labeling (Sample Discussion)' },
  { value: 'it_services', label: 'IT Services (Consultation)' },
  { value: 'electronics_appliances', label: 'Electronics & Appliances (Product Demo)' },
  { value: 'furniture_interiors', label: 'Furniture & Interiors (Site Measurement)' },
  { value: 'agri_inputs', label: 'Agri Inputs (Advisory Call)' },
];

const DOMAIN_SETTINGS: Record<
  DomainType,
  DomainSettingsPreset
> = {
  saas: {
    label: 'demo',
    terms: {
      book: ['book demo', 'schedule demo', 'product demo', 'demo call'],
      update: ['reschedule demo', 'change demo', 'move demo'],
      cancel: ['cancel demo', 'drop demo'],
      info: ['demo details', 'demo availability', 'demo slots'],
    },
    domainContext: {
      industry: 'SaaS',
      business_type: 'software product company',
      company_description: 'Provides software products, subscriptions, onboarding, feature guidance, and product demonstrations.',
      products_or_services: ['software plans', 'product demo', 'pricing', 'implementation support'],
      domain_vocabulary: ['demo', 'trial', 'subscription', 'plan', 'onboarding', 'integration', 'feature'],
      user_goals: ['explore product fit', 'compare plans', 'understand pricing', 'book a demo'],
      important_entities: ['plan', 'team size', 'use case', 'integration', 'budget', 'timeline'],
      tone_guidelines: ['professional', 'helpful', 'concise'],
    },
  },
  real_estate: {
    label: 'site visit',
    terms: {
      book: ['site visit', 'property tour', 'visit property', 'schedule visit'],
      update: ['reschedule visit', 'change visit', 'move visit'],
      cancel: ['cancel visit', 'cancel tour'],
      info: ['visit slots', 'tour availability', 'visit details'],
    },
    domainContext: {
      industry: 'Real Estate',
      business_type: 'property sales and leasing business',
      company_description: 'Helps users discover, compare, and inquire about properties, pricing, amenities, and availability.',
      products_or_services: ['residential property', 'commercial property', 'property pricing', 'site visit'],
      domain_vocabulary: ['flat', 'apartment', 'villa', 'plot', 'BHK', 'amenities', 'possession'],
      user_goals: ['search properties', 'check budget fit', 'compare locations', 'book a site visit'],
      important_entities: ['location', 'budget', 'property type', 'configuration', 'amenities', 'possession date'],
      tone_guidelines: ['consultative', 'clear', 'trustworthy'],
    },
  },
  healthcare: {
    label: 'appointment',
    terms: {
      book: ['book appointment', 'consultation', 'doctor visit', 'schedule consultation'],
      update: ['reschedule appointment', 'change appointment'],
      cancel: ['cancel appointment', 'cancel consultation'],
      info: ['appointment details', 'slot availability', 'clinic timings'],
    },
    domainContext: {
      industry: 'Healthcare',
      business_type: 'clinic or healthcare service provider',
      company_description: 'Supports users with appointments, consultation details, services, and healthcare-related information.',
      products_or_services: ['consultation', 'appointment booking', 'clinic services', 'doctor availability'],
      domain_vocabulary: ['appointment', 'consultation', 'doctor', 'clinic', 'treatment', 'slot'],
      user_goals: ['book appointment', 'check availability', 'understand services', 'get consultation details'],
      important_entities: ['doctor', 'specialty', 'location', 'appointment date', 'appointment time'],
      tone_guidelines: ['empathetic', 'clear', 'professional'],
    },
  },
  education: {
    label: 'counseling session',
    terms: {
      book: ['book counseling', 'counseling session', 'admission counseling'],
      update: ['reschedule counseling', 'change counseling session'],
      cancel: ['cancel counseling', 'cancel session'],
      info: ['counseling details', 'session availability'],
    },
    domainContext: {
      industry: 'Education',
      business_type: 'education or admissions counseling provider',
      company_description: 'Guides students and families on courses, admissions, eligibility, fees, and counseling sessions.',
      products_or_services: ['courses', 'admission support', 'counseling session', 'fee information'],
      domain_vocabulary: ['course', 'admission', 'counseling', 'eligibility', 'batch', 'fee structure'],
      user_goals: ['explore courses', 'check eligibility', 'understand fees', 'book counseling'],
      important_entities: ['course', 'grade', 'budget', 'intake', 'location', 'eligibility'],
      tone_guidelines: ['supportive', 'clear', 'informative'],
    },
  },
  retail: {
    label: 'store visit',
    terms: {
      book: ['book store visit', 'schedule store visit', 'visit store', 'store appointment'],
      update: ['reschedule store visit', 'change store visit'],
      cancel: ['cancel store visit'],
      info: ['store visit details', 'store timings', 'visit availability'],
    },
    domainContext: {
      industry: 'Retail',
      business_type: 'local and multi-category retail business',
      company_description: 'Handles product discovery, stock checks, pricing, offers, and in-store purchase assistance for retail buyers.',
      products_or_services: ['product inquiry', 'stock availability', 'store visit', 'pricing and offers'],
      domain_vocabulary: ['MRP', 'discount', 'stock', 'store visit', 'exchange', 'offer'],
      user_goals: ['check product availability', 'compare pricing', 'visit store', 'understand offers'],
      important_entities: ['product name', 'brand', 'variant', 'price', 'store location', 'availability'],
      tone_guidelines: ['helpful', 'clear', 'sales-assistive'],
    },
  },
  automobile: {
    label: 'test drive',
    terms: {
      book: ['book test drive', 'schedule test drive', 'showroom visit', 'vehicle demo'],
      update: ['reschedule test drive', 'change test drive'],
      cancel: ['cancel test drive', 'cancel showroom visit'],
      info: ['test drive details', 'vehicle availability', 'showroom timings'],
    },
    domainContext: {
      industry: 'Automobile',
      business_type: 'vehicle dealership and auto service business',
      company_description: 'Supports users with vehicle discovery, variants, financing, servicing, accessories, and test drive scheduling.',
      products_or_services: ['vehicle inquiry', 'test drive', 'service booking', 'auto accessories'],
      domain_vocabulary: ['variant', 'on-road price', 'mileage', 'EMI', 'test drive', 'service interval'],
      user_goals: ['compare models', 'check on-road price', 'book test drive', 'schedule service'],
      important_entities: ['vehicle model', 'variant', 'budget', 'fuel type', 'city', 'appointment time'],
      tone_guidelines: ['consultative', 'clear', 'trustworthy'],
    },
  },
  travel: {
    label: 'travel consultation',
    terms: {
      book: ['book travel consultation', 'schedule travel consultation', 'trip planning call', 'travel meeting'],
      update: ['reschedule travel consultation', 'change travel meeting'],
      cancel: ['cancel travel consultation', 'cancel travel meeting'],
      info: ['travel consultation details', 'trip planning availability', 'meeting details'],
    },
    domainContext: {
      industry: 'Travel',
      business_type: 'travel agency and tour planning business',
      company_description: 'Helps customers with tour packages, bookings, visa assistance, and travel planning based on destination and budget.',
      products_or_services: ['tour packages', 'travel consultation', 'visa support', 'booking assistance'],
      domain_vocabulary: ['package', 'itinerary', 'visa', 'departure', 'hotel', 'travel consultation'],
      user_goals: ['explore packages', 'plan itinerary', 'check visa help', 'book consultation'],
      important_entities: ['destination', 'budget', 'travel dates', 'traveler count', 'hotel preference', 'visa need'],
      tone_guidelines: ['friendly', 'clear', 'advisory'],
    },
  },
  finance_insurance: {
    label: 'consultation',
    terms: {
      book: ['book consultation', 'schedule consultation', 'financial consultation', 'insurance consultation'],
      update: ['reschedule consultation', 'change consultation'],
      cancel: ['cancel consultation'],
      info: ['consultation details', 'advisor availability', 'slot availability'],
    },
    domainContext: {
      industry: 'Finance and Insurance',
      business_type: 'financial advisory, lending, and insurance services',
      company_description: 'Assists customers with loans, policies, renewals, investments, and financial guidance.',
      products_or_services: ['loan consultation', 'insurance policy inquiry', 'renewal support', 'investment advisory'],
      domain_vocabulary: ['EMI', 'premium', 'policy', 'coverage', 'interest rate', 'eligibility'],
      user_goals: ['understand plans', 'check eligibility', 'compare policies', 'book consultation'],
      important_entities: ['loan amount', 'policy type', 'income range', 'tenure', 'city', 'appointment time'],
      tone_guidelines: ['professional', 'reassuring', 'precise'],
    },
  },
  legal_services: {
    label: 'legal consultation',
    terms: {
      book: ['book legal consultation', 'schedule legal consultation', 'lawyer meeting', 'consult advocate'],
      update: ['reschedule legal consultation', 'change lawyer meeting'],
      cancel: ['cancel legal consultation'],
      info: ['consultation details', 'lawyer availability', 'meeting details'],
    },
    domainContext: {
      industry: 'Legal Services',
      business_type: 'legal advisory and documentation services',
      company_description: 'Guides users on legal consultation, registrations, compliance, contracts, and related documentation needs.',
      products_or_services: ['legal consultation', 'documentation support', 'registration assistance', 'compliance guidance'],
      domain_vocabulary: ['agreement', 'registration', 'compliance', 'notice', 'filing', 'consultation'],
      user_goals: ['understand legal process', 'get documentation help', 'book consultation', 'check compliance support'],
      important_entities: ['case type', 'business type', 'document type', 'jurisdiction', 'timeline', 'consultation date'],
      tone_guidelines: ['professional', 'clear', 'measured'],
    },
  },
  home_services: {
    label: 'service visit',
    terms: {
      book: ['book service visit', 'schedule service visit', 'technician visit', 'inspection visit'],
      update: ['reschedule service visit', 'change technician visit'],
      cancel: ['cancel service visit'],
      info: ['service visit details', 'technician availability', 'inspection details'],
    },
    domainContext: {
      industry: 'Home Services',
      business_type: 'repair, maintenance, and installation service business',
      company_description: 'Supports users with home repairs, maintenance requests, site inspection, and installation bookings.',
      products_or_services: ['repair request', 'installation service', 'inspection visit', 'maintenance support'],
      domain_vocabulary: ['technician', 'inspection', 'installation', 'service charge', 'visit slot', 'warranty'],
      user_goals: ['book service', 'check charges', 'request inspection', 'reschedule technician visit'],
      important_entities: ['service type', 'location', 'preferred slot', 'issue description', 'product type', 'urgency'],
      tone_guidelines: ['practical', 'clear', 'responsive'],
    },
  },
  manufacturing: {
    label: 'business meeting',
    terms: {
      book: ['book business meeting', 'schedule business meeting', 'factory meeting', 'sample discussion'],
      update: ['reschedule business meeting', 'change factory meeting'],
      cancel: ['cancel business meeting'],
      info: ['meeting details', 'sample discussion details', 'availability'],
    },
    domainContext: {
      industry: 'Manufacturing',
      business_type: 'manufacturing and industrial supply business',
      company_description: 'Handles B2B product inquiries, specifications, sample requests, MOQ discussions, and quotation meetings.',
      products_or_services: ['product specifications', 'bulk quotation', 'sample discussion', 'factory meeting'],
      domain_vocabulary: ['MOQ', 'specification', 'sample', 'lead time', 'quotation', 'bulk order'],
      user_goals: ['request quotation', 'check specifications', 'discuss MOQ', 'book business meeting'],
      important_entities: ['product type', 'quantity', 'specification', 'timeline', 'city', 'budget'],
      tone_guidelines: ['professional', 'technical', 'business-focused'],
    },
  },
  wholesale_distribution: {
    label: 'dealer meeting',
    terms: {
      book: ['book dealer meeting', 'schedule dealer meeting', 'distributor meeting', 'supply discussion'],
      update: ['reschedule dealer meeting', 'change distributor meeting'],
      cancel: ['cancel dealer meeting'],
      info: ['dealer meeting details', 'supply discussion details', 'availability'],
    },
    domainContext: {
      industry: 'Wholesale and Distribution',
      business_type: 'wholesale supply and dealer network business',
      company_description: 'Supports dealer onboarding, stock inquiries, bulk pricing, and distributor partnership discussions.',
      products_or_services: ['bulk inquiry', 'dealer onboarding', 'stock availability', 'supply discussion'],
      domain_vocabulary: ['dealer', 'distributor', 'bulk order', 'stockist', 'margin', 'supply'],
      user_goals: ['check wholesale pricing', 'become dealer', 'confirm stock', 'book dealer meeting'],
      important_entities: ['product category', 'quantity', 'region', 'dealer type', 'pricing', 'stock status'],
      tone_guidelines: ['commercial', 'clear', 'relationship-oriented'],
    },
  },
  hospitality: {
    label: 'booking consultation',
    terms: {
      book: ['book consultation', 'schedule booking consultation', 'venue visit', 'banquet discussion'],
      update: ['reschedule consultation', 'change venue visit'],
      cancel: ['cancel consultation', 'cancel venue visit'],
      info: ['booking details', 'venue availability', 'consultation details'],
    },
    domainContext: {
      industry: 'Hospitality',
      business_type: 'hotel, venue, and guest service business',
      company_description: 'Handles room bookings, venue reservations, banquet inquiries, and guest assistance for hospitality businesses.',
      products_or_services: ['room booking', 'venue inquiry', 'banquet consultation', 'guest assistance'],
      domain_vocabulary: ['room availability', 'banquet', 'check-in', 'package', 'venue visit', 'booking'],
      user_goals: ['check availability', 'compare packages', 'book venue visit', 'understand pricing'],
      important_entities: ['property type', 'event date', 'guest count', 'budget', 'room category', 'location'],
      tone_guidelines: ['polite', 'service-oriented', 'clear'],
    },
  },
  beauty_wellness: {
    label: 'appointment',
    terms: {
      book: ['book appointment', 'schedule appointment', 'salon appointment', 'wellness consultation'],
      update: ['reschedule appointment', 'change appointment'],
      cancel: ['cancel appointment'],
      info: ['appointment details', 'slot availability', 'service details'],
    },
    domainContext: {
      industry: 'Beauty and Wellness',
      business_type: 'salon, spa, and wellness service business',
      company_description: 'Helps customers with treatment details, service packages, appointment booking, and wellness consultations.',
      products_or_services: ['appointment booking', 'service inquiry', 'package details', 'wellness consultation'],
      domain_vocabulary: ['treatment', 'session', 'package', 'slot', 'skin care', 'appointment'],
      user_goals: ['book appointment', 'check packages', 'understand treatment', 'compare services'],
      important_entities: ['service type', 'preferred slot', 'budget', 'location', 'staff preference', 'package'],
      tone_guidelines: ['warm', 'polished', 'helpful'],
    },
  },
  fitness: {
    label: 'trial session',
    terms: {
      book: ['book trial session', 'schedule trial', 'gym trial', 'fitness consultation'],
      update: ['reschedule trial', 'change trial session'],
      cancel: ['cancel trial session'],
      info: ['trial details', 'class timings', 'slot availability'],
    },
    domainContext: {
      industry: 'Fitness',
      business_type: 'gym, fitness studio, or wellness training business',
      company_description: 'Assists users with memberships, trial sessions, fitness classes, personal training, and wellness programs.',
      products_or_services: ['trial session', 'membership inquiry', 'class information', 'fitness consultation'],
      domain_vocabulary: ['membership', 'trial', 'class timing', 'trainer', 'batch', 'fitness goal'],
      user_goals: ['book trial', 'compare memberships', 'check class timings', 'talk to trainer'],
      important_entities: ['fitness goal', 'preferred slot', 'membership plan', 'location', 'budget', 'trainer preference'],
      tone_guidelines: ['motivating', 'clear', 'supportive'],
    },
  },
  events_wedding_services: {
    label: 'consultation',
    terms: {
      book: ['book consultation', 'schedule consultation', 'wedding consultation', 'event planning meeting'],
      update: ['reschedule consultation', 'change planning meeting'],
      cancel: ['cancel consultation'],
      info: ['consultation details', 'planner availability', 'meeting details'],
    },
    domainContext: {
      industry: 'Events and Wedding Services',
      business_type: 'event planning and wedding services business',
      company_description: 'Supports customers with packages, availability, planning discussions, and booking consultations for events and weddings.',
      products_or_services: ['event consultation', 'package inquiry', 'planning meeting', 'booking support'],
      domain_vocabulary: ['package', 'decor', 'vendor', 'event date', 'planning', 'consultation'],
      user_goals: ['compare packages', 'check event availability', 'book consultation', 'understand planning support'],
      important_entities: ['event type', 'event date', 'guest count', 'budget', 'location', 'service package'],
      tone_guidelines: ['warm', 'organized', 'reassuring'],
    },
  },
  printing_signage: {
    label: 'project discussion',
    terms: {
      book: ['book project discussion', 'schedule project discussion', 'print consultation', 'design meeting'],
      update: ['reschedule project discussion', 'change design meeting'],
      cancel: ['cancel project discussion'],
      info: ['project discussion details', 'design meeting details', 'availability'],
    },
    domainContext: {
      industry: 'Printing and Signage',
      business_type: 'printing, branding, and signage production business',
      company_description: 'Handles customer requirements for printing, branding, signage, production specs, and quotation discussions.',
      products_or_services: ['print inquiry', 'signage quotation', 'design meeting', 'branding discussion'],
      domain_vocabulary: ['flex', 'vinyl', 'offset', 'size', 'finish', 'quotation'],
      user_goals: ['request quote', 'share requirements', 'book project discussion', 'confirm production specs'],
      important_entities: ['print type', 'size', 'quantity', 'material', 'timeline', 'budget'],
      tone_guidelines: ['practical', 'clear', 'detail-oriented'],
    },
  },
  packaging_labeling: {
    label: 'sample discussion',
    terms: {
      book: ['book sample discussion', 'schedule sample discussion', 'packaging meeting', 'label consultation'],
      update: ['reschedule sample discussion', 'change packaging meeting'],
      cancel: ['cancel sample discussion'],
      info: ['sample discussion details', 'packaging meeting details', 'availability'],
    },
    domainContext: {
      industry: 'Packaging and Labeling',
      business_type: 'packaging and labeling supply business',
      company_description: 'Supports businesses with packaging options, label customization, sample reviews, and bulk order discussions.',
      products_or_services: ['sample discussion', 'label inquiry', 'packaging quote', 'bulk order support'],
      domain_vocabulary: ['label', 'packaging', 'sample', 'MOQ', 'custom box', 'printing'],
      user_goals: ['request sample', 'compare packaging options', 'book discussion', 'get bulk quote'],
      important_entities: ['packaging type', 'size', 'quantity', 'material', 'branding need', 'timeline'],
      tone_guidelines: ['professional', 'clear', 'solution-oriented'],
    },
  },
  it_services: {
    label: 'consultation',
    terms: {
      book: ['book consultation', 'schedule consultation', 'project discussion', 'tech consultation'],
      update: ['reschedule consultation', 'change project discussion'],
      cancel: ['cancel consultation'],
      info: ['consultation details', 'project discussion details', 'availability'],
    },
    domainContext: {
      industry: 'IT Services',
      business_type: 'software and technology services company',
      company_description: 'Helps clients with software development, implementation, support, maintenance, and technical consultations.',
      products_or_services: ['project consultation', 'implementation discussion', 'support inquiry', 'technology advisory'],
      domain_vocabulary: ['implementation', 'integration', 'support', 'deployment', 'scope', 'technical consultation'],
      user_goals: ['discuss requirements', 'book consultation', 'understand scope', 'request support'],
      important_entities: ['project type', 'team size', 'timeline', 'budget', 'integration need', 'technology stack'],
      tone_guidelines: ['professional', 'technical', 'clear'],
    },
  },
  electronics_appliances: {
    label: 'product demo',
    terms: {
      book: ['book product demo', 'schedule product demo', 'store demo', 'installation visit'],
      update: ['reschedule demo', 'change installation visit'],
      cancel: ['cancel product demo', 'cancel installation visit'],
      info: ['demo details', 'installation details', 'availability'],
    },
    domainContext: {
      industry: 'Electronics and Appliances',
      business_type: 'consumer electronics and appliance business',
      company_description: 'Assists customers with product comparison, demos, installation planning, warranty support, and service requests.',
      products_or_services: ['product demo', 'appliance inquiry', 'installation support', 'warranty support'],
      domain_vocabulary: ['demo', 'warranty', 'installation', 'service center', 'model', 'specification'],
      user_goals: ['compare models', 'book demo', 'request installation', 'understand warranty'],
      important_entities: ['product model', 'brand', 'budget', 'service type', 'location', 'preferred slot'],
      tone_guidelines: ['helpful', 'clear', 'product-focused'],
    },
  },
  furniture_interiors: {
    label: 'site measurement',
    terms: {
      book: ['book site measurement', 'schedule site measurement', 'interior consultation', 'design discussion'],
      update: ['reschedule site measurement', 'change design discussion'],
      cancel: ['cancel site measurement'],
      info: ['site measurement details', 'design consultation details', 'availability'],
    },
    domainContext: {
      industry: 'Furniture and Interiors',
      business_type: 'furniture retail and interior design business',
      company_description: 'Guides users on furniture selection, interior design consultations, site measurements, customization, and quotations.',
      products_or_services: ['interior consultation', 'site measurement', 'furniture inquiry', 'quotation support'],
      domain_vocabulary: ['modular', 'site measurement', 'layout', 'finish', 'quotation', 'customization'],
      user_goals: ['explore furniture options', 'book site visit', 'get quotation', 'discuss customization'],
      important_entities: ['space type', 'location', 'budget', 'style', 'timeline', 'measurement slot'],
      tone_guidelines: ['consultative', 'clear', 'design-aware'],
    },
  },
  agri_inputs: {
    label: 'advisory call',
    terms: {
      book: ['book advisory call', 'schedule advisory', 'dealer discussion', 'product guidance call'],
      update: ['reschedule advisory call', 'change dealer discussion'],
      cancel: ['cancel advisory call'],
      info: ['advisory details', 'dealer discussion details', 'availability'],
    },
    domainContext: {
      industry: 'Agriculture Inputs',
      business_type: 'agri inputs and farm support business',
      company_description: 'Supports dealers and farmers with seeds, fertilizers, equipment guidance, and advisory discussions.',
      products_or_services: ['product inquiry', 'dealer discussion', 'advisory call', 'input guidance'],
      domain_vocabulary: ['seed', 'fertilizer', 'crop cycle', 'dealer', 'yield', 'advisory'],
      user_goals: ['check product suitability', 'talk to advisor', 'become dealer', 'compare options'],
      important_entities: ['crop type', 'season', 'location', 'land size', 'product category', 'dealer type'],
      tone_guidelines: ['practical', 'clear', 'supportive'],
    },
  },
};

const listToTextarea = (items: string[]) => items.join('\n');

const buildCompanySettingsState = (domain: DomainType): PlaygroundCompanySettingsState => {
  const preset = DOMAIN_SETTINGS[domain];
  return {
    scheduleLabel: preset.label,
    schedulingTerms: {
      book: listToTextarea(preset.terms.book),
      update: listToTextarea(preset.terms.update),
      cancel: listToTextarea(preset.terms.cancel),
      info: listToTextarea(preset.terms.info),
    },
    domainContext: {
      industry: preset.domainContext.industry,
      businessType: preset.domainContext.business_type,
      companyDescription: preset.domainContext.company_description,
      productsOrServices: listToTextarea(preset.domainContext.products_or_services),
      domainVocabulary: listToTextarea(preset.domainContext.domain_vocabulary),
      userGoals: listToTextarea(preset.domainContext.user_goals),
      importantEntities: listToTextarea(preset.domainContext.important_entities),
      toneGuidelines: listToTextarea(preset.domainContext.tone_guidelines),
    },
  };
};

const parseTextareaList = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const isDomainType = (value: string): value is DomainType =>
  DOMAIN_OPTIONS.some((item) => item.value === value);



const Playground: React.FC<PlaygroundProps> = ({ token }) => {
  // KB & Prompt
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [kbLoading, setKbLoading] = useState(true);
  const [selectedKbId, setSelectedKbId] = useState<number | null>(null);

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [selectedChatbotType, setSelectedChatbotType] = useState<ChatbotType>('Default');
  const [selectedDomain, setSelectedDomain] = useState<DomainType>('saas');
  const [playgroundCompanySettings, setPlaygroundCompanySettings] = useState<PlaygroundCompanySettingsState>(
    () => buildCompanySettingsState('saas')
  );

  // Conversations from DB
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [convLoading, setConvLoading] = useState(true);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);

  // Messages from DB (for active conversation)
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgsLoading, setMsgsLoading] = useState(false);

  // Compose / send
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // ------------------------------------------
  // Fetch KBs + Prompts + initial conversations
  // ------------------------------------------
  useEffect(() => {
    const fetchKBs = async () => {
      setKbLoading(true);
      try {
        const res = await api.get(`/api/kb/list`, { headers: authHeaders });
        const kbs: KnowledgeBase[] = res.data.knowledgeBases || [];
        setKnowledgeBases(kbs);
        if (!selectedKbId && kbs.length > 0) setSelectedKbId(kbs[0].id);
      } catch (e) {
        console.error(e);
        setErrorBanner('Failed to load knowledge bases.');
      } finally {
        setKbLoading(false);
      }
    };

    const fetchPrompts = async () => {
      setPromptsLoading(true);
      try {
        const res = await api.get(`/prompt/list`, { headers: authHeaders });
        setPrompts(res.data.prompts || []);
      } catch (e) {
        console.error(e);
      } finally {
        setPromptsLoading(false);
      }
    };

    fetchKBs();
    fetchPrompts();
  }, [authHeaders, selectedKbId]);

  const fetchConversations = async (kbId?: number | null) => {
    setConvLoading(true);
    try {
      const params = new URLSearchParams();
      if (kbId) params.set('kb_id', String(kbId));
      params.set('status', 'active');
      params.set('limit', '50');
      const res = await api.get(`/ai/conversations?${params.toString()}`, { headers: authHeaders });
      const list: Conversation[] = res.data?.conversations || [];
      setConversations(list);
      // Pick first as active if none selected
      if (!activeConvId && list.length > 0) {
        setActiveConvId(list[0].id);
        setSelectedKbId(list[0].kbId);
      }
    } catch (e) {
      console.error(e);
      setErrorBanner('Failed to load conversations.');
    } finally {
      setConvLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations(selectedKbId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKbId]);

  // ------------------------------------------
  // Load messages for active conversation
  // ------------------------------------------
  const loadMessages = async (conversationId: string) => {
    setMsgsLoading(true);
    try {
      const res = await api.get(`/ai/conversations/${conversationId}/messages?order=asc&limit=500`, { headers: authHeaders });
      const msgs: Message[] = res.data?.messages || [];
      setMessages(msgs);
    } catch (e) {
      console.error(e);
      setErrorBanner('Failed to load messages.');
    } finally {
      setMsgsLoading(false);
      // scroll after render
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    }
  };

  useEffect(() => {
    if (activeConvId) {
      // ensure selected KB reflects the conversation
      const conv = conversations.find((c) => c.id === activeConvId);
      if (conv) setSelectedKbId(conv.kbId);
      loadMessages(activeConvId);
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConvId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  // ------------------------------------------
  // Conversation helpers
  // ------------------------------------------
  const kbName = (id?: number | null) =>
    id ? knowledgeBases.find((k) => k.id === id)?.name ?? `KB #${id}` : 'No KB';

  const createConversation = async (kbId: number, title?: string) => {
    const body: any = { kb_id: kbId };
    if (title && title.trim()) body.title = title.trim();
    const res = await api.post(`/ai/conversations`, body, { headers: authHeaders });
    const convo: Conversation = res.data?.conversation;
    // refresh list & set active
    await fetchConversations(selectedKbId);
    setActiveConvId(convo.id);
    return convo.id;
  };

  const renameConversation = async (cid: string) => {
    const current = conversations.find((c) => c.id === cid);
    if (!current) return;
    const name = window.prompt('Rename conversation:', current.title || 'Untitled chat');
    if (!name) return;
    await api.patch(`/ai/conversations/${cid}`, { title: name }, { headers: authHeaders });
    await fetchConversations(selectedKbId);
  };

  const deleteConversation = async (cid: string) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    await api.delete(`/ai/conversations/${cid}`, { headers: authHeaders });
    await fetchConversations(selectedKbId);
    setActiveConvId((prev) => {
      const remaining = conversations.filter((c) => c.id !== cid);
      return remaining[0]?.id ?? null;
    });
    setMessages([]);
  };

  const onNewChat = async () => {
    if (!selectedKbId) {
      setErrorBanner('Select a knowledge base first.');
      return;
    }
    const id = await createConversation(selectedKbId, '');
    setActiveConvId(id);
    setMessages([]);
  };

  // ------------------------------------------
  // KB / Prompt change behavior
  // ------------------------------------------
  const onChangeKb = async (newKbId: number) => {
    // If current chat has messages, recommend new chat to keep memory clean
    if (messages.length > 0) {
      if (!window.confirm('Switching knowledge base will start a new chat to keep memory consistent. Continue?')) {
        return;
      }
      setSelectedKbId(newKbId);
      const id = await createConversation(newKbId, 'Untitled Chat');
      setActiveConvId(id);
      setMessages([]);
    } else {
      setSelectedKbId(newKbId);
      await fetchConversations(newKbId);
    }
  };

  const onChangePrompt = async (val: string) => {
    const newId = val === '' ? null : Number(val);
    // If current chat has messages, recommend new chat for clean comparison
    if (messages.length > 0) {
      if (!window.confirm('Changing prompt will start a new chat so results are clean. Continue?')) {
        return;
      }
      setSelectedPromptId(newId);
      if (selectedKbId) {
        const id = await createConversation(selectedKbId, 'Untitled Chat');
        setActiveConvId(id);
        setMessages([]);
      }
    } else {
      setSelectedPromptId(newId);
    }
  };

  const onChangeChatbotType = async (val: string) => {
    const newType: ChatbotType =
      val === 'ConversationalChatbot' ? 'ConversationalChatbot' : 'Default';

    if (newType === selectedChatbotType) return;

    // If current chat has messages, recommend new chat for clean comparison
    if (messages.length > 0) {
      if (!window.confirm('Changing chatbot type will start a new chat so results are clean. Continue?')) {
        return;
      }
      setSelectedChatbotType(newType);
      if (selectedKbId) {
        const id = await createConversation(selectedKbId, 'Untitled Chat');
        setActiveConvId(id);
        setMessages([]);
      }
    } else {
      setSelectedChatbotType(newType);
    }
  };

  const onChangeDomain = async (val: string) => {
    const newDomain: DomainType = isDomainType(val) ? val : 'saas';

    if (newDomain === selectedDomain) return;

    if (messages.length > 0) {
      if (!window.confirm('Changing domain will start a new chat so results are clean. Continue?')) {
        return;
      }
      setSelectedDomain(newDomain);
      setPlaygroundCompanySettings(buildCompanySettingsState(newDomain));
      if (selectedKbId) {
        const id = await createConversation(selectedKbId, 'Untitled Chat');
        setActiveConvId(id);
        setMessages([]);
      }
    } else {
      setSelectedDomain(newDomain);
      setPlaygroundCompanySettings(buildCompanySettingsState(newDomain));
    }
  };

  const updateScheduleSetting = (
    field: keyof PlaygroundCompanySettingsState['schedulingTerms'] | 'scheduleLabel',
    value: string
  ) => {
    setPlaygroundCompanySettings((prev) => {
      if (field === 'scheduleLabel') {
        return { ...prev, scheduleLabel: value };
      }
      return {
        ...prev,
        schedulingTerms: {
          ...prev.schedulingTerms,
          [field]: value,
        },
      };
    });
  };

  const updateDomainContextField = (field: keyof DomainContextFormState, value: string) => {
    setPlaygroundCompanySettings((prev) => ({
      ...prev,
      domainContext: {
        ...prev.domainContext,
        [field]: value,
      },
    }));
  };

  // ------------------------------------------
  // Send message
  // ------------------------------------------
  const sendMessage = async () => {
    setErrorBanner(null);
    if (!composer.trim()) return;
    if (!selectedKbId) {
      setErrorBanner('Please select a knowledge base.');
      return;
    }

    // ensure an active conversation exists
    let cid = activeConvId;
    if (!cid) {
      cid = await createConversation(selectedKbId, 'Untitled Chat');
      setActiveConvId(cid);
    }

    setSending(true);
    try {
      const payload: any = {
        prompt: composer.trim(),
        kb_id: selectedKbId,
        conversation_id: cid,
        chatbotType: selectedChatbotType,
        companysettings: {
          schedule_object_label: playgroundCompanySettings.scheduleLabel.trim() || DOMAIN_SETTINGS[selectedDomain].label,
          scheduling_terms: {
            book: parseTextareaList(playgroundCompanySettings.schedulingTerms.book),
            update: parseTextareaList(playgroundCompanySettings.schedulingTerms.update),
            cancel: parseTextareaList(playgroundCompanySettings.schedulingTerms.cancel),
            info: parseTextareaList(playgroundCompanySettings.schedulingTerms.info),
          },
          domain_context: {
            industry: playgroundCompanySettings.domainContext.industry.trim(),
            business_type: playgroundCompanySettings.domainContext.businessType.trim(),
            company_description: playgroundCompanySettings.domainContext.companyDescription.trim(),
            products_or_services: parseTextareaList(playgroundCompanySettings.domainContext.productsOrServices),
            domain_vocabulary: parseTextareaList(playgroundCompanySettings.domainContext.domainVocabulary),
            user_goals: parseTextareaList(playgroundCompanySettings.domainContext.userGoals),
            important_entities: parseTextareaList(playgroundCompanySettings.domainContext.importantEntities),
            tone_guidelines: parseTextareaList(playgroundCompanySettings.domainContext.toneGuidelines),
          },
        },
      };
      if (selectedPromptId !== null) payload.prompt_id = selectedPromptId;

      // Optimistic UI: show user message immediately
      const provisional: Message = {
        id: -Date.now(), // temp id
        role: 'user',
        content: composer.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, provisional]);
      setComposer('');

      const res = await api.post(`/ai/query`, payload, { headers: authHeaders });

      const insufficientContext = Boolean(res.data?.insufficient_context);
      const insufficientCount = Number(res.data?.insufficient_context_count ?? 0);
      if (insufficientContext && insufficientCount > 1) {
        console.log(
          '[Playground] Response hidden due to insufficient_context_count:',
          insufficientCount
        );
        return;
      }

      // Append assistant message from response
      const assistant: Message = {
        id: -Date.now() - 1, // temp id
        role: 'assistant',
        content: res.data?.response || 'No response',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistant]);

      // Finally, refresh from DB so we’re consistent with server history
      await loadMessages(cid!);
      await fetchConversations(selectedKbId); // update timestamps/counts
    } catch (e: any) {
      console.error(e);
      const msg = e?.response?.data?.message || e.message || 'Error occurred';
      setErrorBanner(msg);
    } finally {
      setSending(false);
    }
  };

  // ------------------------------------------
  // Render helpers
  // ------------------------------------------
  const ConversationItem: React.FC<{ c: Conversation; active: boolean }> = ({ c, active }) => (
    <div
      onClick={() => setActiveConvId(c.id)}
      className={`p-3 rounded-lg cursor-pointer border ${active ? 'bg-white border-blue-400 shadow-sm' : 'bg-gray-50 hover:bg-white border-transparent'
        }`}
      title={new Date(c.updatedAt).toLocaleString()}
    >
      <div className="text-sm font-semibold text-gray-800 line-clamp-1">{c.title || 'Untitled chat'}</div>
      <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">{kbName(c.kbId)}</span>
        <span>· {new Date(c.updatedAt).toLocaleTimeString()}</span>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            renameConversation(c.id);
          }}
          className="text-xs text-blue-700 hover:underline"
        >
          Rename
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteConversation(c.id);
          }}
          className="text-xs text-red-600 hover:underline"
        >
          Delete
        </button>
      </div>
    </div>
  );

  const MessageBubble: React.FC<{ m: Message }> = ({ m }) => (
    <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} my-2`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white text-gray-900 border border-gray-200'
          }`}
      >
        <div className="whitespace-pre-wrap">{m.content}</div>
      </div>
    </div>
  );

  // ------------------------------------------
  // UI
  // ------------------------------------------
  return (

    <div className="w-full min-h-screen bg-white">
      <div className="grid grid-cols-12 h-screen">
        {/* Sidebar: fixed (no scroll) */}
        <aside className="col-span-3 border-r border-gray-200 bg-gray-50 p-4 flex flex-col h-screen">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Conversations</h3>
            <button
              onClick={onNewChat}
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
              disabled={!selectedKbId && knowledgeBases.length === 0}
              title={!selectedKbId && knowledgeBases.length === 0 ? 'Select a KB first' : 'Start a new chat'}
            >
              New Chat
            </button>
          </div>

          {/* KB selector */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Knowledge Base</label>
            {kbLoading ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : knowledgeBases.length === 0 ? (
              <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                No KBs. Upload one first.
              </div>
            ) : (
              <select
                value={selectedKbId ?? ''}
                onChange={(e) => onChangeKb(Number(e.target.value))}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
              >
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Prompt selector */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Prompt</label>
            {promptsLoading ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : (
              <select
                value={selectedPromptId ?? ''}
                onChange={(e) => onChangePrompt(e.target.value)}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
              >
                <option value="">Auto (system default)</option>
                {prompts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.isSystemDefined ? ' • System' : ''}
                    {p.isActive ? ' • Active' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Chatbot Type selector */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Chatbot Type</label>
            <select
              value={selectedChatbotType}
              onChange={(e) => onChangeChatbotType(e.target.value)}
              className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
            >
              <option value="Default">Default</option>
              <option value="ConversationalChatbot">ConversationalChatbot</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Domain</label>
            <select
              value={selectedDomain}
              onChange={(e) => onChangeDomain(e.target.value)}
              className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
            >
              {DOMAIN_OPTIONS.map((domain) => (
                <option key={domain.value} value={domain.value}>
                  {domain.label}
                </option>
              ))}
            </select>
          </div>

          <details className="mb-4 rounded border border-gray-200 bg-white" open>
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-700">
              Scheduling Settings
            </summary>
            <div className="space-y-3 border-t border-gray-200 px-3 py-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scheduling Label</label>
                <input
                  value={playgroundCompanySettings.scheduleLabel}
                  onChange={(e) => updateScheduleSetting('scheduleLabel', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Book Terms</label>
                <textarea
                  rows={2}
                  value={playgroundCompanySettings.schedulingTerms.book}
                  onChange={(e) => updateScheduleSetting('book', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Update Terms</label>
                <textarea
                  rows={2}
                  value={playgroundCompanySettings.schedulingTerms.update}
                  onChange={(e) => updateScheduleSetting('update', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cancel Terms</label>
                <textarea
                  rows={2}
                  value={playgroundCompanySettings.schedulingTerms.cancel}
                  onChange={(e) => updateScheduleSetting('cancel', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Info Terms</label>
                <textarea
                  rows={2}
                  value={playgroundCompanySettings.schedulingTerms.info}
                  onChange={(e) => updateScheduleSetting('info', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
            </div>
          </details>

          <details className="mb-4 rounded border border-gray-200 bg-white" open>
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-700">
              Domain Context
            </summary>
            <div className="space-y-3 border-t border-gray-200 px-3 py-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Industry</label>
                <input
                  value={playgroundCompanySettings.domainContext.industry}
                  onChange={(e) => updateDomainContextField('industry', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Business Type</label>
                <input
                  value={playgroundCompanySettings.domainContext.businessType}
                  onChange={(e) => updateDomainContextField('businessType', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Company Description</label>
                <textarea
                  rows={3}
                  value={playgroundCompanySettings.domainContext.companyDescription}
                  onChange={(e) => updateDomainContextField('companyDescription', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Products or Services</label>
                <textarea
                  rows={3}
                  value={playgroundCompanySettings.domainContext.productsOrServices}
                  onChange={(e) => updateDomainContextField('productsOrServices', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Domain Vocabulary</label>
                <textarea
                  rows={3}
                  value={playgroundCompanySettings.domainContext.domainVocabulary}
                  onChange={(e) => updateDomainContextField('domainVocabulary', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Common User Goals</label>
                <textarea
                  rows={3}
                  value={playgroundCompanySettings.domainContext.userGoals}
                  onChange={(e) => updateDomainContextField('userGoals', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Important Entities or Filters</label>
                <textarea
                  rows={3}
                  value={playgroundCompanySettings.domainContext.importantEntities}
                  onChange={(e) => updateDomainContextField('importantEntities', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tone Guidelines</label>
                <textarea
                  rows={2}
                  value={playgroundCompanySettings.domainContext.toneGuidelines}
                  onChange={(e) => updateDomainContextField('toneGuidelines', e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded bg-white resize-y"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Use one item per line for list fields. Changing the domain resets these defaults for clean testing.
              </p>
            </div>
          </details>

          {/* Conversations list (scrollable) */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {convLoading ? (
              <div className="text-sm text-gray-500">Loading conversations…</div>
            ) : conversations.length === 0 ? (
              <div className="text-sm text-gray-500">No conversations yet. Start a new chat.</div>
            ) : (
              conversations
                .filter((c) => c.status === 'active')
                .map((c) => <ConversationItem key={c.id} c={c} active={c.id === activeConvId} />)
            )}
          </div>
        </aside>

        {/* Main: chat (only transcript scrolls) */}
        <main className="col-span-9 flex flex-col h-screen">
          {/* Header (fixed) */}
          <div className="border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {conversations.find((c) => c.id === activeConvId)?.title || 'Playground'}
              </h2>
              <div className="text-sm text-gray-500">
                KB:{' '}
                <span className="font-medium">
                  {kbName(conversations.find((c) => c.id === activeConvId)?.kbId ?? selectedKbId)}
                </span>
                {activeConvId && <span className="ml-2 text-gray-400">• id: {activeConvId}</span>}
              </div>
            </div>
          </div>

          {/* Error */}
          {errorBanner && (
            <div className="m-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm">
              {errorBanner}
            </div>
          )}

          {/* Transcript (scrollable) */}
          <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-white to-gray-50">
            {msgsLoading ? (
              <div className="text-sm text-gray-500">Loading messages…</div>
            ) : messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-gray-500 text-sm">Start the conversation by asking a question…</div>
              </div>
            ) : (
              messages.map((m) => <MessageBubble key={`${m.role}-${m.id}-${m.createdAt}`} m={m} />)
            )}
            {sending && (
              <div className="flex items-center gap-2 text-gray-500 text-sm mt-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                <span>Assistant is thinking…</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer (fixed) */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <div className="flex gap-3">
              <textarea
                placeholder="Type your message…"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!sending) sendMessage();
                  }
                }}
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300 resize-none"
                disabled={sending || !selectedKbId}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !selectedKbId || !composer.trim()}
                className={`px-5 h-[42px] self-end rounded-lg font-semibold ${sending || !selectedKbId || !composer.trim()
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                Send
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Press <kbd className="px-1 py-0.5 bg-gray-100 rounded border">Enter</kbd> to send,{' '}
              <kbd className="px-1 py-0.5 bg-gray-100 rounded border">Shift</kbd> +{' '}
              <kbd className="px-1 py-0.5 bg-gray-100 rounded border">Enter</kbd> for a new line.
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export { Playground };
export default Playground;
